import React, { useEffect, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard, Shield, Lock, ChevronRight, ChevronDown, CheckCircle2, Sparkles, Clock, Video, FileText, Star, Package, Headphones, Gift, ArrowLeft, Users, LogIn, UserPlus, Trash2, ShoppingCart, Check, Info, Crown } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { useToast } from '../components/ui/Toast';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../i18n/LanguageContext';
import { useUser } from '../contexts/UserContext';
import { usePackages } from '../contexts/PackagesContext';
interface CheckoutPageProps {
  onNavigate: (page: string) => void;
}
interface AddOnService {
  id: string;
  title: string;
  price: number;
}
interface AddOnCategory {
  id: string;
  title: string;
  icon: React.ElementType;
  color: string;
  services: AddOnService[];
  isNavigable?: boolean;
}
const ALL_ADDON_CATEGORIES: AddOnCategory[] = [{
  id: 'reports',
  title: 'Отчёты',
  icon: FileText,
  color: 'indigo',
  services: [{
    id: 'addon-natal',
    title: 'Натальная карта Premium',
    price: 3900
  }, {
    id: 'addon-compatibility',
    title: 'Совместимость Premium',
    price: 4900
  }, {
    id: 'addon-solar',
    title: 'Соляр Premium',
    price: 4400
  }, {
    id: 'addon-forecast',
    title: 'Прогноз (Транзиты) Premium',
    price: 3900
  }, {
    id: 'addon-combo',
    title: 'Комбо: Натал + Соляр',
    price: 6900
  }]
}, {
  id: 'consultations',
  title: 'Консультации',
  icon: Headphones,
  color: 'sky',
  services: [],
  isNavigable: true
}, {
  id: 'packages',
  title: 'Пакеты',
  icon: Gift,
  color: 'emerald',
  services: [{
    id: 'addon-pkg20',
    title: 'Пакет 20 отчётов',
    price: 76000
  }, {
    id: 'addon-pkg50',
    title: 'Пакет 50 отчётов',
    price: 180000
  }, {
    id: 'addon-pkg100',
    title: 'Пакет 100 отчётов',
    price: 315000
  }]
}];
// Helper to get all flat services for price calculation
const ALL_FLAT_SERVICES = ALL_ADDON_CATEGORIES.flatMap(c => c.services);
export function CheckoutPage({
  onNavigate
}: CheckoutPageProps) {
  const {
    success
  } = useToast();
  const {
    t
  } = useLanguage();
  const {
    items,
    removeItem,
    mainItem,
    clearCart
  } = useCart();
  const {
    activePackages,
    totalRemaining,
    useReport,
    getOldestActivePackage
  } = usePackages();
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'sbp'>('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const {
    isLoggedIn,
    user
  } = useUser();
  // Subscription discount for astrologers
  const isAstrologer = user?.role === 'astrologer';
  const currentPlan = isAstrologer ? 'PRO' : 'FREE';
  const subDiscount = currentPlan === 'TOP' ? 20 : currentPlan === 'PRO' ? 10 : 0;
  // Collapsible state for categories (left)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['reports']));
  // Collapsible state for cart items (right)
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  // Initialize expanded items based on cart items
  useEffect(() => {
    if (items.length > 0) {
      setExpandedItems(new Set([items[0].id]));
    }
  }, [items.length]);
  // Use all cart items for pricing
  const basePrice = items.length > 0 ? items.reduce((sum, i) => sum + i.price, 0) : 0;
  const toggleAddOn = (id: string) => {
    setSelectedAddOns(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
  };
  const toggleCategory = (id: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const toggleCartItem = (id: string) => {
    if (items.length <= 1) return; // Don't collapse if only 1 item
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const addOnsTotal = ALL_FLAT_SERVICES.filter(s => selectedAddOns.includes(s.id)).reduce((sum, s) => sum + s.price, 0);
  const subDiscountAmount = subDiscount > 0 ? Math.round((basePrice + addOnsTotal) * subDiscount / 100) : 0;
  const subtotal = basePrice + addOnsTotal;
  const promoDiscountAmount = promoApplied ? promoDiscount : 0;
  const discount = subDiscountAmount + promoDiscountAmount;
  const total = Math.max(0, basePrice + addOnsTotal - discount);
  // Package payment logic
  const hasActivePackage = activePackages.length > 0;
  const canPayWithPackage = hasActivePackage && items.every(item => item.type === 'premium-report' || item.type === 'consultation');
  const [payWithPackage, setPayWithPackage] = useState(canPayWithPackage);
  const handleApplyPromo = () => {
    if (promoCode.trim().toLowerCase() === 'astro10') {
      setPromoDiscount(Math.round(subtotal * 0.1));
      setPromoApplied(true);
    } else if (promoCode.trim().toLowerCase() === 'astro20') {
      setPromoDiscount(Math.round(subtotal * 0.2));
      setPromoApplied(true);
    } else {
      setPromoApplied(true);
      setPromoDiscount(500);
    }
  };
  const handlePayment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms || !isLoggedIn) return;
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      if (payWithPackage && canPayWithPackage) {
        const pkg = getOldestActivePackage();
        if (pkg) {
          items.forEach(item => {
            useReport(pkg.id, {
              orderId: `ord-${Date.now()}`,
              reportType: item.type === 'consultation' ? 'consultation' : 'report',
              reportTitle: item.name,
              source: item.astrologer ? 'astrologer' : 'ai',
              astrologerName: item.astrologer?.name
            });
          });
          success('Оплачено из пакета отчетов');
        }
      } else {
        success(t('checkout.success'));
      }
      clearCart();
      onNavigate('order-confirmation');
    }, 1500);
  };
  const steps = [{
    label: t('checkout.step.product'),
    done: true
  }, {
    label: t('checkout.step.checkout'),
    active: true
  }, {
    label: t('checkout.step.payment'),
    done: false
  }];
  const formatPrice = (p: number) => p.toLocaleString('ru-RU') + ' ₽';
  const getColorClasses = (color: string) => {
    switch (color) {
      case 'indigo':
        return 'text-indigo-600 bg-indigo-100 border-indigo-200';
      case 'sky':
        return 'text-sky-600 bg-sky-100 border-sky-200';
      case 'emerald':
        return 'text-emerald-600 bg-emerald-100 border-emerald-200';
      case 'amber':
        return 'text-amber-600 bg-amber-100 border-amber-200';
      default:
        return 'text-slate-600 bg-slate-100 border-slate-200';
    }
  };
  return <div className="w-full bg-slate-50 min-h-screen pb-32 lg:pb-24" data-id="element-22640">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 pt-6 pb-6" data-id="element-22641">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22642">
          <Breadcrumbs items={[{
          label: t('checkout.breadcrumb.home'),
          onClick: () => onNavigate('home')
        }, {
          label: t('checkout.breadcrumb.marketplace'),
          onClick: () => onNavigate('marketplace')
        }, {
          label: t('checkout.breadcrumb.title')
        }]} className="mb-6" data-id="element-22643" />

          <div className="flex items-center justify-between mb-6" data-id="element-22644">
            <div className="flex items-center gap-3" data-id="element-22645">
              <button onClick={() => onNavigate('product-detail')} className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:border-slate-300 transition-colors" aria-label={t('checkout.back')} data-id="element-22646">
                <ArrowLeft className="w-4 h-4" data-id="element-22647" />
              </button>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-id="element-22648">
                {t('checkout.title')}
              </h1>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-2" data-id="element-22649">
            {steps.map((step, i) => <Fragment key={step.label} data-id="element-22650">
                <div className="flex items-center gap-2" data-id="element-22651">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step.done ? 'bg-emerald-500 text-white' : step.active ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-500'}`} data-id="element-22652">
                    {step.done ? <CheckCircle2 className="w-4 h-4" data-id="element-22653" /> : i + 1}
                  </div>
                  <span className={`text-sm font-medium ${step.active ? 'text-slate-900' : step.done ? 'text-emerald-600' : 'text-slate-400'}`} data-id="element-22654">
                    {step.label}
                  </span>
                </div>
                {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 rounded ${step.done ? 'bg-emerald-400' : 'bg-slate-200'}`} data-id="element-22655" />}
              </Fragment>)}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-8" data-id="element-22656">
        {subDiscount > 0 && <div className="mb-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl flex items-center gap-3" data-id="element-22657">
            <Crown className="w-5 h-5 text-indigo-600 flex-shrink-0" data-id="element-22658" />
            <div data-id="element-22659">
              <span className="font-bold text-slate-900" data-id="element-22660">
                Тариф {currentPlan}
              </span>
              <span className="text-sm text-slate-600 ml-2" data-id="element-22661">
                — скидка{' '}
                <span className="font-bold text-indigo-600" data-id="element-22662">
                  {subDiscount}%
                </span>{' '}
                на весь заказ применена автоматически
              </span>
            </div>
          </div>}
        <form onSubmit={handlePayment} className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-22663">
          {/* Left Column */}
          <div className="lg:col-span-7 space-y-6" data-id="element-22664">
            {/* Add-on Services (Collapsible) */}
            <Card padding="lg" data-id="element-22665">
              <div className="flex items-center gap-2 mb-2" data-id="element-22666">
                <Sparkles className="w-5 h-5 text-amber-500" data-id="element-22667" />
                <h2 className="text-xl font-bold text-slate-900" data-id="element-22668">
                  {t('checkout.addons.title')}
                </h2>
              </div>
              <p className="text-sm text-slate-500 mb-6" data-id="element-22669">
                {t('checkout.addons.subtitle')}
              </p>

              <div className="space-y-3" data-id="element-22670">
                {ALL_ADDON_CATEGORIES.map(category => {
                const Icon = category.icon;
                const isExpanded = expandedCategories.has(category.id);
                const selectedCount = category.services.filter(s => selectedAddOns.includes(s.id)).length;
                const colorClasses = getColorClasses(category.color);
                return <div key={category.id} className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-id="element-22671">
                      <button type="button" onClick={() => toggleCategory(category.id)} className="w-full flex items-center justify-between p-3.5 hover:bg-slate-50 transition-colors" data-id="element-22672">
                        <div className="flex items-center gap-3" data-id="element-22673">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClasses}`} data-id="element-22674">
                            <Icon className="w-4 h-4" data-id="element-22675" />
                          </div>
                          <span className="font-semibold text-slate-900 text-sm" data-id="element-22676">
                            {category.title}
                          </span>
                          {selectedCount > 0 && <span className="bg-indigo-100 text-indigo-700 text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-22677">
                              {selectedCount}
                            </span>}
                        </div>
                        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-22678" />
                      </button>

                      <AnimatePresence initial={false} data-id="element-22679">
                        {isExpanded && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.2,
                      ease: 'easeInOut'
                    }} data-id="element-22680">
                            <div className="px-3 pb-3 pt-1 space-y-1.5 border-t border-slate-100" data-id="element-22681">
                              {(category as any).isNavigable ? <div className="p-3 text-center" data-id="element-22682">
                                  <p className="text-xs text-slate-500 mb-3" data-id="element-22683">
                                    Цена зависит от выбранного астролога и
                                    длительности
                                  </p>
                                  <button type="button" onClick={() => onNavigate('astrologers')} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-50 border border-sky-200 text-sky-700 hover:bg-sky-100 transition-colors text-sm font-medium" data-id="element-22684">
                                    <Users className="w-4 h-4" data-id="element-22685" />
                                    Выбрать астролога
                                  </button>
                                </div> : category.services.map(service => {
                          const isSelected = selectedAddOns.includes(service.id);
                          return <label key={service.id} className={`flex items-center justify-between p-2.5 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50/80 border border-indigo-200' : 'hover:bg-slate-50 border border-transparent'}`} data-id="element-22686">
                                      <div className="flex items-center gap-3 min-w-0" data-id="element-22687">
                                        <input type="checkbox" checked={isSelected} onChange={() => toggleAddOn(service.id)} className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0" data-id="element-22688" />
                                        <span className={`text-sm truncate ${isSelected ? 'font-medium text-indigo-900' : 'text-slate-700'}`} data-id="element-22689">
                                          {service.title}
                                        </span>
                                      </div>
                                      <span className={`text-sm font-medium tabular-nums whitespace-nowrap ml-3 ${isSelected ? 'text-indigo-600' : 'text-slate-500'}`} data-id="element-22690">
                                        +{formatPrice(service.price)}
                                      </span>
                                    </label>;
                        })}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>;
              })}
              </div>
            </Card>

            {/* Auth Gate */}
            {!isLoggedIn && <Card padding="lg" className="border-amber-200 bg-amber-50/30" data-id="element-22691">
                <div className="text-center py-4" data-id="element-22692">
                  <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4" data-id="element-22693">
                    <LogIn className="w-7 h-7 text-amber-600" data-id="element-22694" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-22695">
                    {t('checkout.auth.title')}
                  </h2>
                  <p className="text-sm text-slate-600 mb-6 max-w-sm mx-auto" data-id="element-22696">
                    {t('checkout.auth.desc')}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-xs mx-auto" data-id="element-22697">
                    <Button variant="primary" onClick={() => onNavigate('auth')} icon={<LogIn className="w-4 h-4" data-id="element-22699" />} className="flex-1" data-id="element-22698">
                      {t('checkout.auth.login')}
                    </Button>
                    <Button variant="outline" onClick={() => onNavigate('auth')} icon={<UserPlus className="w-4 h-4" data-id="element-22701" />} className="flex-1" data-id="element-22700">
                      {t('checkout.auth.register')}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-4 flex items-center justify-center gap-1" data-id="element-22702">
                    <Shield className="w-3 h-3" data-id="element-22703" />
                    {t('checkout.auth.hint')}
                  </p>
                </div>
              </Card>}

            {/* Payment Method */}
            <Card padding="lg" className={!isLoggedIn ? 'opacity-40 pointer-events-none select-none' : ''} data-id="element-22704">
              <h2 className="text-xl font-bold text-slate-900 mb-6" data-id="element-22705">
                {t('checkout.payment.title')}
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-22706">
                <label className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-colors ${paymentMethod === 'card' ? 'border-indigo-500 bg-indigo-50/30' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-22707">
                  <input type="radio" name="payment" value="card" checked={paymentMethod === 'card'} onChange={() => setPaymentMethod('card')} className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-500" data-id="element-22708" />
                  <CreditCard className={`w-6 h-6 ml-3 mr-3 ${paymentMethod === 'card' ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-22709" />
                  <span className="font-medium text-slate-900" data-id="element-22710">
                    {t('checkout.payment.card')}
                  </span>
                </label>

                <label className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-colors ${paymentMethod === 'sbp' ? 'border-indigo-500 bg-indigo-50/30' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-22711">
                  <input type="radio" name="payment" value="sbp" checked={paymentMethod === 'sbp'} onChange={() => setPaymentMethod('sbp')} className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-500" data-id="element-22712" />
                  <div className={`w-6 h-6 ml-3 mr-3 rounded bg-slate-100 flex items-center justify-center font-bold text-xs ${paymentMethod === 'sbp' ? 'text-indigo-600' : 'text-slate-500'}`} data-id="element-22713">
                    СБП
                  </div>
                  <span className="font-medium text-slate-900" data-id="element-22714">
                    {t('checkout.payment.sbp')}
                  </span>
                </label>
              </div>

              {paymentMethod === 'card' && <motion.div initial={{
              opacity: 0,
              y: -8
            }} animate={{
              opacity: 1,
              y: 0
            }} className="space-y-4" data-id="element-22715">
                  <Input label={t('checkout.card.number')} placeholder="0000 0000 0000 0000" required data-id="element-22716" />
                  <div className="grid grid-cols-2 gap-4" data-id="element-22717">
                    <Input label={t('checkout.card.expiry')} placeholder="MM/YY" required data-id="element-22718" />
                    <Input label={t('checkout.card.cvc')} placeholder="123" type="password" maxLength={3} required data-id="element-22719" />
                  </div>
                  <Input label={t('checkout.card.name')} placeholder="IVAN IVANOV" required data-id="element-22720" />
                </motion.div>}

              {paymentMethod === 'sbp' && <motion.div initial={{
              opacity: 0,
              y: -8
            }} animate={{
              opacity: 1,
              y: 0
            }} className="text-center py-8" data-id="element-22721">
                  <div className="w-48 h-48 bg-slate-100 rounded-2xl mx-auto mb-4 flex items-center justify-center border-2 border-dashed border-slate-300" data-id="element-22722">
                    <span className="text-slate-400 font-medium" data-id="element-22723">
                      {t('checkout.sbp.qr')}
                    </span>
                  </div>
                  <p className="text-slate-600 text-sm" data-id="element-22724">
                    {t('checkout.sbp.hint')}
                  </p>
                </motion.div>}
            </Card>

            {/* Contact Info */}
            <Card padding="lg" className={!isLoggedIn ? 'opacity-40 pointer-events-none select-none' : ''} data-id="element-22725">
              <h2 className="text-xl font-bold text-slate-900 mb-6" data-id="element-22726">
                {t('checkout.contact.title')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-22727">
                <Input label={t('checkout.contact.name')} placeholder={t('checkout.contact.namePlaceholder')} required defaultValue="Анна" data-id="element-22728" />
                <Input label={t('checkout.contact.email')} type="email" placeholder="mail@example.com" required defaultValue="anna@example.com" data-id="element-22729" />
              </div>
              <Input label={t('checkout.contact.phone')} placeholder={t('checkout.contact.phonePlaceholder')} className="mt-4" data-id="element-22730" />
              <p className="text-xs text-slate-500 mt-4 flex items-center" data-id="element-22731">
                <Lock className="w-3 h-3 mr-1.5" data-id="element-22732" />{' '}
                {t('checkout.contact.privacy')}
              </p>
            </Card>
          </div>

          {/* Right Column: Order Summary */}
          <div className="lg:col-span-5" data-id="element-22733">
            <div className="sticky top-24 space-y-6" data-id="element-22734">
              <Card padding="lg" className="border-indigo-100 shadow-lg" data-id="element-22735">
                <h3 className="text-xl font-bold text-slate-900 mb-6" data-id="element-22736">
                  {t('checkout.order.title')}
                </h3>

                {/* Cart Items (Collapsible) */}
                <div className="space-y-3 mb-4" data-id="element-22737">
                  {items.length > 0 ? items.map((item, idx) => {
                  const isExpanded = items.length === 1 || expandedItems.has(item.id);
                  return <motion.div key={item.id} initial={{
                    opacity: 0,
                    y: 8
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    x: -20
                  }} transition={{
                    delay: idx * 0.05
                  }} className={`bg-gradient-to-br from-indigo-50 to-slate-50 rounded-xl border border-indigo-100 relative group overflow-hidden ${items.length > 1 ? 'cursor-pointer hover:border-indigo-200' : ''}`} onClick={() => toggleCartItem(item.id)} data-id="element-22738">
                          {/* Collapsed Header */}
                          <div className="p-3.5 flex items-center justify-between gap-3" data-id="element-22739">
                            <div className="flex items-center gap-2.5 min-w-0 flex-1" data-id="element-22740">
                              <div className="w-2 h-2 rounded-full bg-indigo-400 flex-shrink-0" data-id="element-22741" />
                              <h4 className="font-bold text-slate-900 text-sm truncate" data-id="element-22742">
                                {item.name}
                              </h4>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0" data-id="element-22743">
                              <span className="text-sm font-bold text-slate-900 tabular-nums" data-id="element-22744">
                                {formatPrice(item.price)}
                              </span>
                              <button type="button" onClick={e => {
                          e.stopPropagation();
                          removeItem(item.id);
                        }} className="w-6 h-6 rounded-full flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors" aria-label={t('checkout.delete')} data-id="element-22745">
                                <Trash2 className="w-3 h-3" data-id="element-22746" />
                              </button>
                              {items.length > 1 && <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-22747" />}
                            </div>
                          </div>

                          {/* Expanded Content */}
                          <AnimatePresence initial={false} data-id="element-22748">
                            {isExpanded && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.2,
                        ease: 'easeInOut'
                      }} data-id="element-22749">
                                <div className="px-4 pb-4 pt-1 border-t border-indigo-100/50" data-id="element-22750">
                                  <div className="flex items-center gap-2 mb-2" data-id="element-22751">
                                    {item.badges?.map((badge, i) => <Badge key={i} variant={badge.variant as any} className="text-[10px]" data-id="element-22752">
                                        {badge.label}
                                      </Badge>)}
                                  </div>

                                  <p className="text-[11px] text-slate-500 leading-relaxed line-clamp-2 mb-2" data-id="element-22753">
                                    {item.description}
                                  </p>

                                  {(item.format || item.duration) && <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500" data-id="element-22754">
                                      {item.format && <span className="flex items-center" data-id="element-22755">
                                          <Video className="w-3 h-3 mr-1 text-indigo-400" data-id="element-22756" />
                                          {item.format}
                                        </span>}
                                      {item.duration && <span className="flex items-center" data-id="element-22757">
                                          <Clock className="w-3 h-3 mr-1 text-indigo-400" data-id="element-22758" />
                                          {item.duration}
                                        </span>}
                                    </div>}

                                  {item.astrologer && <div className="flex items-center gap-2 mt-3 pt-2 border-t border-indigo-100/80" data-id="element-22759">
                                      <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-700 flex-shrink-0" data-id="element-22760">
                                        {item.astrologer.initials}
                                      </div>
                                      <div className="text-xs font-medium text-slate-700" data-id="element-22761">
                                        {item.astrologer.name}
                                      </div>
                                      <div className="flex items-center gap-0.5 ml-auto" data-id="element-22762">
                                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-22763" />
                                        <span className="text-[11px] font-medium text-slate-600" data-id="element-22764">
                                          {item.astrologer.rating}
                                        </span>
                                      </div>
                                    </div>}

                                  {!item.astrologer && <div className="flex items-center gap-2 mt-3 pt-2 border-t border-indigo-100/80" data-id="element-22765">
                                      <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-22766">
                                        <FileText className="w-3.5 h-3.5 text-indigo-700" data-id="element-22767" />
                                      </div>
                                      <div className="text-xs font-medium text-slate-700" data-id="element-22768">
                                        {t('checkout.order.autoGeneration')}
                                      </div>
                                    </div>}
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </motion.div>;
                }) : <div className="text-center py-6" data-id="element-22769">
                      <ShoppingCart className="w-8 h-8 text-slate-300 mx-auto mb-2" data-id="element-22770" />
                      <p className="text-sm text-slate-400" data-id="element-22771">
                        {t('checkout.order.empty')}
                      </p>
                    </div>}

                  <button type="button" onClick={() => onNavigate('marketplace')} className="w-full py-2.5 px-4 rounded-xl border-2 border-dashed border-slate-200 text-sm text-slate-500 hover:border-indigo-300 hover:text-indigo-600 transition-colors flex items-center justify-center gap-2" data-id="element-22772">
                    <span className="text-lg leading-none" data-id="element-22773">+</span>
                    {t('checkout.order.addMore')}
                  </button>
                </div>

                {/* Selected Add-ons */}
                {selectedAddOns.length > 0 && <div className="mb-4 pb-4 border-b border-slate-100 space-y-3" data-id="element-22774">
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-22775">
                      {t('checkout.order.addonsLabel')}
                    </div>
                    {ALL_FLAT_SERVICES.filter(s => selectedAddOns.includes(s.id)).map(addon => {
                  return <div key={addon.id} className="flex items-center justify-between" data-id="element-22776">
                          <div className="flex items-center gap-2" data-id="element-22777">
                            <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" data-id="element-22778" />
                            <span className="text-sm text-slate-700" data-id="element-22779">
                              {addon.title}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-slate-700 tabular-nums whitespace-nowrap" data-id="element-22780">
                            +{formatPrice(addon.price)}
                          </span>
                        </div>;
                })}
                  </div>}

                {/* Promo Code */}
                <div className="mb-6" data-id="element-22781">
                  <div className="flex items-center gap-2" data-id="element-22782">
                    <div className="flex-1" data-id="element-22783">
                      <Input placeholder={t('checkout.promo.placeholder')} value={promoCode} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPromoCode(e.target.value)} disabled={promoApplied} data-id="element-22784" />
                    </div>
                    <Button variant="outline" type="button" onClick={handleApplyPromo} disabled={promoApplied || !promoCode.trim()} className="flex-shrink-0 h-[42px]" data-id="element-22785">
                      {promoApplied ? t('checkout.promo.applied') : t('checkout.promo.apply')}
                    </Button>
                  </div>
                  {promoApplied && <motion.p initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} className="text-xs text-emerald-600 mt-2 flex items-center" data-id="element-22786">
                      <Gift className="w-3 h-3 mr-1" data-id="element-22787" />{' '}
                      {t('checkout.promo.success')}
                    </motion.p>}
                </div>

                {/* Package Payment Block */}
                {isAstrologer && items.every(item => item.type === 'premium-report' || item.type === 'consultation') && <>
                      {hasActivePackage && canPayWithPackage ? <div className="mb-6 p-4 bg-indigo-50 border border-indigo-100 rounded-xl" data-id="element-22788">
                          <div className="flex items-start gap-3" data-id="element-22789">
                            <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0 mt-0.5" data-id="element-22790">
                              <Package className="w-4 h-4" data-id="element-22791" />
                            </div>
                            <div className="w-full" data-id="element-22792">
                              <h4 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-22793">
                                Этот отчет будет списан из пакета
                              </h4>
                              <div className="space-y-1 mb-3" data-id="element-22794">
                                <div className="flex justify-between text-xs" data-id="element-22795">
                                  <span className="text-indigo-700" data-id="element-22796">
                                    Пакет:
                                  </span>
                                  <span className="font-bold text-indigo-900" data-id="element-22797">
                                    {getOldestActivePackage()?.title || 'Активный пакет'}
                                  </span>
                                </div>
                                <div className="flex justify-between text-xs" data-id="element-22798">
                                  <span className="text-indigo-700" data-id="element-22799">
                                    Осталось:
                                  </span>
                                  <span className="font-bold text-indigo-900" data-id="element-22800">
                                    {totalRemaining}
                                  </span>
                                </div>
                                <div className="flex justify-between text-xs" data-id="element-22801">
                                  <span className="text-indigo-700" data-id="element-22802">
                                    После заказа:
                                  </span>
                                  <span className="font-bold text-indigo-900" data-id="element-22803">
                                    {totalRemaining - items.length}
                                  </span>
                                </div>
                              </div>

                              <label className="flex items-start gap-2 cursor-pointer group pt-2 border-t border-indigo-100/50" data-id="element-22804">
                                <div className="relative flex items-center justify-center mt-0.5" data-id="element-22805">
                                  <input type="checkbox" className="peer sr-only" checked={payWithPackage} onChange={e => setPayWithPackage(e.target.checked)} data-id="element-22806" />
                                  <div className="w-4 h-4 border-2 border-indigo-300 rounded peer-checked:bg-indigo-600 peer-checked:border-indigo-600 transition-colors" data-id="element-22807" />
                                  <Check className="w-3 h-3 text-white absolute opacity-0 peer-checked:opacity-100 transition-opacity" data-id="element-22808" />
                                </div>
                                <div className="text-sm text-indigo-900 font-medium group-hover:text-indigo-700 transition-colors" data-id="element-22809">
                                  Использовать пакет для оплаты
                                </div>
                              </label>
                            </div>
                          </div>
                        </div> : <div className="mb-6 p-4 bg-emerald-50 border border-emerald-100 rounded-xl" data-id="element-22810">
                          <div className="flex items-start gap-3" data-id="element-22811">
                            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-22812">
                              <Package className="w-4 h-4" data-id="element-22813" />
                            </div>
                            <div className="w-full" data-id="element-22814">
                              <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-22815">
                                Пакет выгоднее
                              </h4>
                              <div className="space-y-1 mb-3" data-id="element-22816">
                                <div className="flex justify-between text-xs" data-id="element-22817">
                                  <span className="text-emerald-700" data-id="element-22818">
                                    Цена разовая:
                                  </span>
                                  <span className="font-bold text-emerald-900" data-id="element-22819">
                                    {formatPrice(basePrice)}
                                  </span>
                                </div>
                                <div className="flex justify-between text-xs" data-id="element-22820">
                                  <span className="text-emerald-700" data-id="element-22821">
                                    Цена в пакете:
                                  </span>
                                  <span className="font-bold text-emerald-900" data-id="element-22822">
                                    от 3 600 ₽
                                  </span>
                                </div>
                                <div className="flex justify-between text-xs" data-id="element-22823">
                                  <span className="text-emerald-700" data-id="element-22824">
                                    Ваша цена по подписке:
                                  </span>
                                  <span className="font-bold text-emerald-900" data-id="element-22825">
                                    от 3 240 ₽
                                  </span>
                                </div>
                              </div>
                              <Button variant="primary" size="sm" fullWidth className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onNavigate('pricing')} data-id="element-22826">
                                Купить пакет
                              </Button>
                            </div>
                          </div>
                        </div>}
                    </>}

                {/* Totals */}
                <div className="space-y-2.5 mb-6" data-id="element-22827">
                  {items.length > 0 && <div className="flex justify-between text-sm text-slate-600" data-id="element-22828">
                      <span data-id="element-22829">
                        {items.length > 1 ? t('checkout.total.services', {
                      count: String(items.length)
                    }) : t('checkout.total.service')}
                      </span>
                      <span className="tabular-nums" data-id="element-22830">
                        {formatPrice(basePrice)}
                      </span>
                    </div>}
                  {addOnsTotal > 0 && <div className="flex justify-between text-sm text-slate-600" data-id="element-22831">
                      <span data-id="element-22832">
                        {t('checkout.total.addons', {
                      count: String(selectedAddOns.length)
                    })}
                      </span>
                      <span className="tabular-nums" data-id="element-22833">
                        +{formatPrice(addOnsTotal)}
                      </span>
                    </div>}
                  {subDiscountAmount > 0 && <div className="flex justify-between text-sm text-emerald-600" data-id="element-22834">
                      <span className="flex items-center gap-1" data-id="element-22835">
                        <Crown className="w-3.5 h-3.5" data-id="element-22836" />
                        Скидка {subDiscount}% ({currentPlan})
                      </span>
                      <span className="tabular-nums font-medium" data-id="element-22837">
                        −{formatPrice(subDiscountAmount)}
                      </span>
                    </div>}
                  {promoDiscountAmount > 0 && <div className="flex justify-between text-sm text-emerald-600" data-id="element-22838">
                      <span data-id="element-22839">{t('checkout.total.discount')}</span>
                      <span className="tabular-nums" data-id="element-22840">
                        −{formatPrice(promoDiscountAmount)}
                      </span>
                    </div>}
                  <div className="flex justify-between items-center pt-3 border-t border-slate-200" data-id="element-22841">
                    <span className="text-lg font-bold text-slate-900" data-id="element-22842">
                      {t('checkout.total.total')}
                    </span>
                    <span className="text-2xl font-bold text-indigo-600 tabular-nums" data-id="element-22843">
                      {payWithPackage ? '0 ₽' : formatPrice(total)}
                    </span>
                  </div>
                </div>

                {/* Payment hint */}
                {items.length > 1 && <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-200/60 mb-5" data-id="element-22844">
                    <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-22845" />
                    <p className="text-xs text-amber-700 leading-relaxed" data-id="element-22846">
                      {t('checkout.payment.hint')}
                    </p>
                  </div>}

                {/* Agreement */}
                <label className="flex items-start gap-3 mb-6 cursor-pointer group" data-id="element-22847">
                  <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" data-id="element-22848" />
                  <span className="text-xs text-slate-500 leading-relaxed group-hover:text-slate-700 transition-colors" data-id="element-22849">
                    {t('checkout.agree.text')}{' '}
                    <button type="button" className="text-indigo-600 hover:underline" data-id="element-22850">
                      {t('checkout.agree.terms')}
                    </button>{' '}
                    {t('checkout.agree.and')}{' '}
                    <button type="button" className="text-indigo-600 hover:underline" data-id="element-22851">
                      {t('checkout.agree.privacy')}
                    </button>
                  </span>
                </label>

                {/* Pay Button */}
                <Button type="submit" size="lg" fullWidth disabled={isProcessing || !agreedToTerms || !isLoggedIn} icon={isProcessing ? undefined : <Lock className="w-4 h-4" data-id="element-22853" />} className="shadow-md shadow-indigo-500/20" data-id="element-22852">
                  {isProcessing ? t('checkout.pay.processing') : !isLoggedIn ? t('checkout.pay.loginRequired') : payWithPackage ? 'Оплатить из пакета' : t('checkout.pay.button', {
                  price: formatPrice(total)
                })}
                </Button>

                {!isLoggedIn && <p className="text-[10px] text-amber-600 text-center mt-2" data-id="element-22854">
                    {t('checkout.login.required')}
                  </p>}
                {isLoggedIn && !agreedToTerms && <p className="text-[10px] text-amber-600 text-center mt-2" data-id="element-22855">
                    {t('checkout.agree.required')}
                  </p>}

                {/* Trust Badges */}
                <div className="mt-6 pt-4 border-t border-slate-100 space-y-2.5" data-id="element-22856">
                  <div className="flex items-center text-xs text-slate-500" data-id="element-22857">
                    <Shield className="w-4 h-4 mr-2 text-emerald-500 flex-shrink-0" data-id="element-22858" />
                    {t('checkout.trust.ssl')}
                  </div>
                  <div className="flex items-center text-xs text-slate-500" data-id="element-22859">
                    <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500 flex-shrink-0" data-id="element-22860" />
                    {t('checkout.trust.refund')}
                  </div>
                  <div className="flex items-center text-xs text-slate-500" data-id="element-22861">
                    <Lock className="w-4 h-4 mr-2 text-emerald-500 flex-shrink-0" data-id="element-22862" />
                    {t('checkout.trust.noSave')}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </form>
      </div>

      {/* Mobile Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between lg:hidden z-50 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]" data-id="element-22863">
        <div data-id="element-22864">
          <div className="text-xs text-slate-500" data-id="element-22865">
            {t('checkout.mobile.total')}
          </div>
          <div className="text-xl font-bold text-slate-900" data-id="element-22866">
            {payWithPackage ? '0 ₽' : formatPrice(total)}
          </div>
        </div>
        <Button size="md" onClick={() => {
        if (agreedToTerms && isLoggedIn) {
          setIsProcessing(true);
          setTimeout(() => {
            setIsProcessing(false);
            if (payWithPackage && canPayWithPackage) {
              const pkg = getOldestActivePackage();
              if (pkg) {
                items.forEach(item => {
                  useReport(pkg.id, {
                    orderId: `ord-${Date.now()}`,
                    reportType: item.type === 'consultation' ? 'consultation' : 'report',
                    reportTitle: item.name,
                    source: item.astrologer ? 'astrologer' : 'ai',
                    astrologerName: item.astrologer?.name
                  });
                });
                success('Оплачено из пакета отчетов');
              }
            } else {
              success(t('checkout.successMobile'));
            }
            clearCart();
            onNavigate('order-confirmation');
          }, 1500);
        }
      }} disabled={isProcessing || !agreedToTerms || !isLoggedIn} className="shadow-md shadow-indigo-500/20" data-id="element-22867">
          {isProcessing ? t('checkout.pay.processing') : payWithPackage ? 'Оплатить из пакета' : t('checkout.mobile.pay')}
        </Button>
      </div>
    </div>;
}